const formatRub = (amount) => `${Math.round(Number(amount) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} \u20BD`;

const rateToPercent = (rate) => {
  if (rate == null || Number.isNaN(Number(rate))) {
    return null;
  }
  const n = Number(rate);
  if (n === 0) {
    return 0;
  }
  const percent = n <= 1 ? n * 100 : n;
  return Math.round(percent * 100) / 100;
};

const formatRate = (rate) => {
  const percent = rateToPercent(rate);
  if (percent == null) {
    return '';
  }
  if (Number.isInteger(percent)) {
    return String(percent);
  }
  return percent.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
};

const yearsLabel = (n) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) {
    return 'лет';
  }
  if (m10 === 1) {
    return 'год';
  }
  if (m10 >= 2 && m10 <= 4) {
    return 'года';
  }
  return 'лет';
};

const formatValueRange = (range) => {
  if (!range || range.length < 2) {
    return '';
  }
  return `${formatRub(range[0])} \u2013 ${formatRub(range[1])}`;
};

const partList = (bank) => (Array.isArray(bank.parts) ? bank.parts : []);

const paymentRowList = (bank) => (Array.isArray(bank.paymentRows) ? bank.paymentRows : []);

const modifierList = (bank) => (Array.isArray(bank.appliedModifiers) ? bank.appliedModifiers : []);

const formatDateOrDuration = (value) => {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'number') {
    return `${value} ${yearsLabel(value)}`;
  }
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `До ${day}.${month}.${date.getFullYear()}`;
  }
  return String(value);
};

const partTypeLabel = (type) => {
  if (type === 'BASE') {
    return 'Базовая';
  }
  if (type === 'COMBO') {
    return 'Комбинированная';
  }
  if (type === 'SUBSIDY') {
    return 'Субсидированная';
  }
  if (type === 'TRANCHE') {
    return 'Траншевая';
  }
  return type || 'часть';
};

const modifierText = (modifier) => {
  const text = modifier.toDisplay || modifier.text || modifier.description || modifier.label;
  if (text) {
    return text;
  }
  if (modifier.type === 'OBJECT_PRICE' && modifier.value != null) {
    return `Стоимость объекта: ${formatRub(Number(modifier.value))}`;
  }
  return '';
};

const childCountLabel = (count) => (count >= 5 ? '5 и более' : String(count));

const familyConditions = (bank) => {
  const lines = [];
  const counts = Array.isArray(bank.childrenCount)
    ? Array.from(new Set(bank.childrenCount)).sort((a, b) => a - b)
    : [];
  const isFamily = counts.length > 0 || Boolean(bank.disabledChildrenCount);
  if (counts.length) {
    lines.push(`Дети: ${counts.map(childCountLabel).join(', ')}`);
  }
  if (bank.disabledChildrenCount) {
    lines.push('Есть ребёнок-инвалид');
  }
  if (isFamily) {
    const displayName = (bank.programDisplayName || '').trim();
    const programName = ((bank.bankProgram && bank.bankProgram.name) || '').trim();
    if (displayName && displayName !== programName) {
      lines.push(`Программа: ${displayName}`);
    }
    if (bank.programChain && bank.programChain.length) {
      lines.push(`Состав: ${bank.programChain.join(' + ')}`);
    }
    if (bank.maxCost) {
      lines.push(`Сумма: до ${formatRub(bank.maxCost)}`);
    }
  }
  const promo = (bank.promoText || '').trim();
  if (promo) {
    lines.push(promo);
  }
  const tooltip = (bank.tooltip || '').trim();
  if (tooltip) {
    lines.push(tooltip);
  }
  return lines;
};

const buildConditions = (bank) => {
  const lines = [];
  if (bank.firstPay) {
    lines.push(`Первоначальный взнос от ${bank.firstPay}%`);
  }
  if (bank.minTerm || bank.maxTerm) {
    lines.push(`Срок ${bank.minTerm || 1}\u2013${bank.maxTerm} лет`);
  }
  partList(bank).forEach((part) => {
    const range = formatValueRange(part.valueRange);
    if (range) {
      const rate = rateToPercent(part.interest);
      const rateText = rate == null ? '' : `, ставка ${rate}%`;
      lines.push(`${partTypeLabel(part.type)}: ${range}${rateText}`);
    }
    const duration = part.matrix && part.matrix[0] && part.matrix[0].duration;
    const matrixRate = rateToPercent(part.matrix && part.matrix[0] && part.matrix[0].interest);
    if (duration && matrixRate != null) {
      lines.push(`Субсидия: ${matrixRate}% первые ${duration} ${yearsLabel(duration)}`);
    }
    const dateLabel = formatDateOrDuration(part.endDateOrDuration);
    if (dateLabel && part.type === 'TRANCHE') {
      lines.push(`Транш: ${dateLabel}`);
    }
  });
  modifierList(bank).forEach((modifier) => {
    if (modifier.type === 'OBJECT_PRICE') {
      const text = modifierText(modifier);
      if (text && !lines.includes(text)) {
        lines.push(text);
      }
    }
  });
  return lines;
};

const rowPayment = (row) => {
  const value = row.monthlyPayment && row.monthlyPayment.value;
  return typeof value === 'number' ? value : null;
};

const summaryPaymentOf = (bank, rows) => {
  if (typeof bank.minMonthlyPayment === 'number') {
    return bank.minMonthlyPayment;
  }
  const first = rows[0] ? rowPayment(rows[0]) : null;
  if (first != null) {
    return first;
  }
  const payments = rows.map(rowPayment).filter((value) => value != null);
  return payments.length ? Math.min.apply(null, payments) : 0;
};

const restrictionForRow = (bank, index) => {
  const part = partList(bank)[index];
  if (!part) {
    return '';
  }
  return formatValueRange(part.valueRange) || partTypeLabel(part.type);
};

const restrictionForPart = (part) => formatValueRange(part.valueRange) || partTypeLabel(part.type);

const orderedParts = (parts) => {
  const base = parts.filter((part) => part.type === 'BASE');
  const combo = parts.filter((part) => part.type === 'COMBO');
  const rest = parts.filter((part) => part.type !== 'BASE' && part.type !== 'COMBO');
  if (combo.length) {
    return [...base, ...combo, ...rest];
  }
  return parts;
};

const periodFallback = (bank, index) => {
  const part = partList(bank)[index];
  const dateLabel = formatDateOrDuration(part && part.endDateOrDuration);
  if (dateLabel) {
    return dateLabel;
  }
  if (part && part.matrix && part.matrix[0] && part.matrix[0].duration) {
    return `Первые ${part.matrix[0].duration} ${yearsLabel(part.matrix[0].duration)}`;
  }
  return bank.maxTerm ? `до ${bank.maxTerm} лет` : '';
};

const rowsFromParts = (bank, parts, paymentRows) => {
  const period = (paymentRows[0] && paymentRows[0].period && paymentRows[0].period.toDisplay) || periodFallback(bank, 0);
  const rows = orderedParts(parts).map((part) => ({
    rate: rateToPercent(part.interest),
    period,
    payment: null,
    restriction: restrictionForPart(part),
  }));
  const blended = paymentRows[0];
  if (blended && (rowPayment(blended) != null || (blended.interest && blended.interest.value != null))) {
    rows.push({
      rate: rateToPercent(blended.interest && blended.interest.value),
      period: (blended.period && blended.period.toDisplay) || period,
      payment: rowPayment(blended),
      restriction: 'Итого',
    });
  }
  return rows;
};

const subsidyMatrixCells = (bank) => {
  const subsidy = partList(bank).find((part) => part.type === 'SUBSIDY');
  return Array.isArray(subsidy && subsidy.matrix) ? subsidy.matrix : [];
};

const minNestedRate = (rows) => {
  const nested = rows
    .filter((row) => row.restriction !== 'Итого')
    .map((row) => row.rate)
    .filter((rate) => rate != null);
  const source = nested.length
    ? nested
    : rows.map((row) => row.rate).filter((rate) => rate != null);
  return source.length ? Math.min.apply(null, source) : null;
};

const summaryRateOf = (bank, rows, isCombo, isCompound) => {
  if (isCombo) {
    const total = [...rows].reverse().find((row) => row.restriction === 'Итого' && row.rate != null);
    if (total && total.rate != null) {
      return total.rate;
    }
  }
  if (isCompound) {
    const min = minNestedRate(rows);
    if (min != null) {
      return min;
    }
  }
  return rateToPercent(bank.interestRate);
};

const buildOfferDisplay = (bank) => {
  const paymentRows = paymentRowList(bank);
  const parts = partList(bank);
  const isCombo = bank.offerType === 'COMBO' || parts.some((part) => part.type === 'COMBO');
  const usePartRows = isCombo || (parts.length > 1 && paymentRows.length <= 1);
  const rows = usePartRows
    ? rowsFromParts(bank, parts, paymentRows)
    : paymentRows.map((row, index) => ({
      rate: rateToPercent(row.interest && row.interest.value),
      period: (row.period && row.period.toDisplay) || periodFallback(bank, index),
      payment: rowPayment(row),
      restriction: restrictionForRow(bank, index),
    }));
  const isCompound = rows.length > 1;

  return {
    isCompound,
    summaryRate: summaryRateOf(bank, rows, isCombo, isCompound),
    summaryFromPrefix: !isCombo,
    summaryPayment: summaryPaymentOf(bank, paymentRows),
    rows,
    conditions: buildConditions(bank),
  };
};

export {
  buildOfferDisplay,
  familyConditions,
  subsidyMatrixCells,
  yearsLabel,
  rateToPercent,
  formatRate,
};
